import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { motion } from "framer-motion";
import type { MotionProps } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNavigate } from "react-router-dom";

export type AddFundsHelpPhase = 0 | 1 | 2 | 3;

const DEMO_BRANDS = [
  {
    id: "b3",
    name: "Windx3",
    domain: "windx3.strike46.com",
    points: 3000,
    created: "Created 14 May 2026, 13:29",
    headerClass:
      "bg-[linear-gradient(125deg,#3d2d5c_0%,#2d2145_48%,#1f1830_100%)]",
    ringClass: "border-purple-400/35",
  },
  {
    id: "b2",
    name: "Windx2",
    domain: "windx2.strike46.com",
    points: 2800,
    created: "Created 12 May 2026, 10:15",
    headerClass:
      "bg-[linear-gradient(125deg,#1a2e24_0%,#15261f_48%,#101c18_100%)]",
    ringClass: "border-emerald-400/35",
  },
  {
    id: "b1",
    name: "Windx1",
    domain: "windx1.strike46.com",
    points: 1500,
    created: "Created 8 May 2026, 09:02",
    headerClass:
      "bg-[linear-gradient(125deg,#1a2438_0%,#151e30_48%,#101828_100%)]",
    ringClass: "border-sky-400/35",
  },
] as const;

const PAY_SUMMARY = {
  name: "Silver",
  price: 998,
  original: 1050,
  points: 3000,
} as const;

const PLATINUM = {
  name: "Platinum",
  points: 250_000,
  price: 78_750,
  original: 87_500,
} as const;

const PULSE_EASE = [0.42, 0, 0.58, 1] as const;
const ACCENT = "rgb(220 38 38)";

function pulseAf(pulseId: string | null, controlId: string): Pick<MotionProps, "animate" | "transition"> {
  const on = pulseId === controlId;
  if (on) {
    return {
      animate: {
        scale: [1, 0.985, 1],
        boxShadow: [
          "0 0 0 0px transparent",
          `0 0 0 3px color-mix(in srgb, ${ACCENT} 50%, transparent)`,
          "0 0 0 0px transparent",
        ],
      },
      transition: { duration: 0.34, ease: PULSE_EASE },
    };
  }
  return { animate: { scale: 1 }, transition: { duration: 0.12, ease: PULSE_EASE } };
}

const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

function formatInr(n: number) {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `₹${n}`;
  }
}

export type AddFundsHelpShellProps = {
  blockPointerForAutopilot?: boolean;
};

export type AddFundsHelpShellRef = {
  pulseControl: (selector: string) => Promise<void>;
  /** Sync visible screen and demo state for tour step index (0–5). */
  prepareForTourStep: (stepIndex: number) => void;
};

export const AddFundsHelpShell = forwardRef<AddFundsHelpShellRef, AddFundsHelpShellProps>(
  function AddFundsHelpShell({ blockPointerForAutopilot = false }, ref) {
    const navigate = useNavigate();
    const [phase, setPhase] = useState<AddFundsHelpPhase>(0);
    const [selectedBrandIdx, setSelectedBrandIdx] = useState(0);
    const [packageKey, setPackageKey] = useState<"silver" | "platinum">("silver");
    const [payMethod, setPayMethod] = useState<"crypto" | "fiat">("crypto");
    const [pulseId, setPulseId] = useState<string | null>(null);

    const brand = DEMO_BRANDS[selectedBrandIdx] ?? DEMO_BRANDS[0];
    const siteUrl = `https://${brand.domain}`;

    const pulseControl = useCallback(async (selector: string) => {
      const id = selector.startsWith("#") ? selector.slice(1) : selector;
      setPulseId(id);
      await delay(420);
      setPulseId(null);
      await delay(120);
    }, []);

    const prepareForTourStep = useCallback((stepIndex: number) => {
      if (stepIndex <= 1) {
        setPhase(0);
      } else if (stepIndex <= 3) {
        setPhase(1);
        setSelectedBrandIdx(0);
      } else if (stepIndex === 4) {
        setPhase(2);
        setSelectedBrandIdx(0);
        setPackageKey("silver");
      } else {
        setPhase(3);
        setSelectedBrandIdx(0);
        setPackageKey("silver");
        setPayMethod("crypto");
      }
    }, []);

    useImperativeHandle(ref, () => ({ pulseControl, prepareForTourStep }), [pulseControl, prepareForTourStep]);

    const block = blockPointerForAutopilot ? " pointer-events-none" : "";

    return (
      <div className="box-border mt-[3.75rem] flex min-h-[calc(100dvh-3.75rem)] flex-col overflow-x-hidden overflow-y-hidden bg-black text-white">
        <div className={`mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col pb-[100px]${block}`}>
          {phase === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col px-3 pb-6 pt-3 sm:px-4">
              <header className="flex shrink-0 items-center gap-2 pb-3">
                <button
                  type="button"
                  onClick={() => navigate("/whitelabel")}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/10"
                  aria-label="Back"
                >
                  <ArrowBackIcon sx={{ fontSize: 24 }} />
                </button>
                <h1 className="m-0 min-w-0 flex-1 text-lg font-semibold text-white">My brands</h1>
                <button
                  type="button"
                  className="rounded-lg bg-red-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white"
                >
                  New
                </button>
              </header>

              <motion.div
                id="wl-af-intro"
                className="mb-3 rounded-lg border border-zinc-800/90 bg-zinc-950/80 px-3 py-2.5"
                {...pulseAf(pulseId, "wl-af-intro")}
              >
                <p className="m-0 text-center text-xs leading-relaxed text-zinc-400">
                 Preview of Add funds flow.
                </p>
              </motion.div>

              <motion.div
                id="wl-af-brand-list"
                className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-4"
                {...pulseAf(pulseId, "wl-af-brand-list")}
              >
                {DEMO_BRANDS.map((b, idx) => (
                  <button
                    key={b.id}
                    type="button"
                    id={idx === 0 ? "wl-af-brand-card-first" : undefined}
                    onClick={() => {
                      setSelectedBrandIdx(idx);
                      setPhase(1);
                    }}
                    className="w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0a0a0a] text-left shadow-[0_8px_28px_rgba(0,0,0,0.45)] transition hover:border-white/15"
                  >
                    <div className={`relative flex items-start gap-3 px-3 py-3 sm:px-4 ${b.headerClass}`}>
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border bg-black/25 text-lg font-bold text-white ${b.ringClass}`}
                      >
                        W
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="m-0 text-base font-bold text-white">{b.name}</p>
                          <span className="flex shrink-0 items-center gap-1 rounded-full border border-sky-500/40 bg-sky-950/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-sky-400" aria-hidden />
                            ACTIVE
                          </span>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-zinc-300/90">{b.domain}</p>
                      </div>
                    </div>
                    <div className="border-t border-white/[0.06] px-3 py-3 sm:px-4">
                      <p className="m-0 text-sm font-semibold text-zinc-200">
                        {b.points.toLocaleString()} points
                      </p>
                      <p className="mt-2 text-[11px] text-zinc-500">{b.created}</p>
                    </div>
                  </button>
                ))}
              </motion.div>
            </div>
          ) : null}

          {phase === 1 ? (
            <div className="flex min-h-0 flex-1 flex-col px-3 pb-6 pt-3 sm:px-4">
              <header className="flex shrink-0 items-center gap-2 pb-4">
                <button
                  type="button"
                  onClick={() => setPhase(0)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-700/80 bg-zinc-900/60 text-white hover:bg-zinc-800"
                  aria-label="Back"
                >
                  <ArrowBackIcon sx={{ fontSize: 22 }} />
                </button>
                <h1 className="m-0 text-xl font-bold text-white">Details</h1>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="rounded-xl border-2 border-emerald-600/55 bg-[#0c0c0c] p-0 shadow-[0_0_24px_rgba(16,185,129,0.08)]">
                  <motion.div
                    id="wl-af-details-balance"
                    className="relative border-b border-white/[0.06] bg-gradient-to-br from-red-950/80 to-black/90 px-3 py-3 sm:px-4"
                    {...pulseAf(pulseId, "wl-af-details-balance")}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="m-0 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
                          Balance
                        </p>
                        <p className="mt-1 text-2xl font-bold text-white">
                          {brand.points.toLocaleString()} points
                        </p>
                      </div>
                      <motion.button
                        id="wl-af-add-funds"
                        type="button"
                        className="shrink-0 rounded-lg bg-red-600 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white shadow-[0_4px_16px_rgba(220,38,38,0.35)]"
                        {...pulseAf(pulseId, "wl-af-add-funds")}
                        onClick={() => setPhase(2)}
                      >
                        Add funds
                      </motion.button>
                    </div>
                  </motion.div>

                  <div className="flex items-center justify-between gap-2 border-b border-white/[0.06] px-3 py-3 sm:px-4">
                    <div className="min-w-0">
                      <p className="m-0 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                        Website
                      </p>
                      <p className="mt-1 truncate font-mono text-xs text-zinc-200 sm:text-sm">{siteUrl}</p>
                    </div>
                    <button
                      type="button"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-white/10 hover:text-white"
                      aria-label="Copy URL"
                    >
                      <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                  </div>

                  <button
                    type="button"
                    className="flex w-full items-center justify-between border-b border-white/[0.06] px-3 py-3 text-left sm:px-4"
                  >
                    <span className="text-sm font-medium text-zinc-200">Portal details</span>
                    <ExpandMoreRoundedIcon sx={{ color: "#71717a" }} />
                  </button>

                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-3 text-left sm:px-4"
                  >
                    <span className="text-sm font-medium text-zinc-200">View transaction history</span>
                    <ChevronRightRoundedIcon sx={{ color: "#71717a" }} />
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {phase === 2 ? (
            <div className="flex min-h-0 flex-1 flex-col px-3 pb-6 pt-3 sm:px-4">
              <header className="flex shrink-0 items-center gap-2 pb-4">
                <button
                  type="button"
                  onClick={() => setPhase(1)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-700/80 bg-zinc-900/60 text-white hover:bg-zinc-800"
                  aria-label="Back"
                >
                  <ArrowBackIcon sx={{ fontSize: 22 }} />
                </button>
                <h1 className="m-0 text-xl font-bold text-white">Packages</h1>
              </header>

              <motion.div
                id="wl-af-packages-section"
                className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden pb-4 [-webkit-overflow-scrolling:touch]"
                {...pulseAf(pulseId, "wl-af-packages-section")}
              >
                <div className="flex gap-3 pb-2" style={{ width: "max-content" }}>
                  <button
                    type="button"
                    id="wl-af-package-silver"
                    onClick={() => setPackageKey("silver")}
                    className={`relative w-[min(78vw,17.5rem)] shrink-0 overflow-hidden rounded-2xl border-2 p-3 text-left transition ${
                      packageKey === "silver"
                        ? "border-red-600 shadow-[0_0_20px_rgba(220,38,38,0.25)]"
                        : "border-zinc-700/80 bg-zinc-900/40"
                    }`}
                  >
                    {packageKey === "silver" ? (
                      <CheckCircleRoundedIcon
                        className="absolute right-2 top-2 text-emerald-500"
                        sx={{ fontSize: 22 }}
                      />
                    ) : null}
                    <p className="m-0 border-b border-red-600/70 pb-1 text-sm font-bold text-white">Silver</p>
                    <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-red-500">
                      <span className="text-base" aria-hidden>
                        ★
                      </span>
                      {PAY_SUMMARY.points.toLocaleString()} points
                    </p>
                    <p className="mt-1 text-lg font-bold text-white">{formatInr(PAY_SUMMARY.original)}</p>
                    <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                      Payment modes
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <span className="rounded-full border border-zinc-600 px-2 py-0.5 text-[10px] text-zinc-300">
                        UPI/Bank transfer
                      </span>
                      <span className="rounded-full border border-zinc-600 px-2 py-0.5 text-[10px] text-zinc-300">
                        Crypto
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPackageKey("platinum")}
                    className="relative w-[min(78vw,17.5rem)] shrink-0 overflow-hidden rounded-2xl border-2 border-zinc-700/80 bg-zinc-900/40 p-3 text-left"
                  >
                    <p className="m-0 border-b border-red-600/40 pb-1 text-sm font-bold text-white">
                      {PLATINUM.name}
                    </p>
                    <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-red-500">
                      <span className="text-base" aria-hidden>
                        ★
                      </span>
                      {PLATINUM.points.toLocaleString()} points
                    </p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-2">
                      <span className="text-lg font-bold text-white">{formatInr(PLATINUM.price)}</span>
                      <span className="text-sm text-zinc-500 line-through">
                        {formatInr(PLATINUM.original)}
                      </span>
                      <span className="text-xs font-semibold text-emerald-500">Save 10%</span>
                    </div>
                    <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                      Payment modes
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <span className="rounded-full border border-zinc-600 px-2 py-0.5 text-[10px] text-zinc-300">
                        UPI/Bank transfer
                      </span>
                      <span className="rounded-full border border-zinc-600 px-2 py-0.5 text-[10px] text-zinc-300">
                        Crypto
                      </span>
                    </div>
                  </button>
                </div>
              </motion.div>

              <div className="mt-auto shrink-0 border-t border-zinc-800/80 pt-3">
                <div className="mb-2 h-0.5 w-full overflow-hidden rounded-full bg-zinc-800">
                  <div className="h-full w-1/3 rounded-full bg-zinc-500" />
                </div>
                <button
                  type="button"
                  onClick={() => setPhase(3)}
                  className="w-full rounded-xl border border-red-900/50 bg-gradient-to-b from-red-950/90 to-black py-3.5 text-sm font-bold text-white"
                >
                  Continue
                </button>
              </div>
            </div>
          ) : null}

          {phase === 3 ? (
            <div className="flex min-h-0 flex-1 flex-col px-3 pb-6 pt-3 sm:px-4">
              <header className="flex shrink-0 items-center gap-2 pb-4">
                <button
                  type="button"
                  onClick={() => setPhase(2)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-700/80 bg-zinc-900/60 text-white hover:bg-zinc-800"
                  aria-label="Back"
                >
                  <ArrowBackIcon sx={{ fontSize: 22 }} />
                </button>
                <h1 className="m-0 text-lg font-bold text-white">Complete payment</h1>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="mx-auto max-w-sm rounded-xl border border-zinc-700/80 bg-zinc-900/50 px-4 py-4 text-center">
                  <p className="m-0 text-base font-semibold text-white">{PAY_SUMMARY.name}</p>
                  <p className="mt-2 text-3xl font-bold text-white">{formatInr(PAY_SUMMARY.price)}</p>
                  <p className="mt-1 text-sm text-zinc-500 line-through">{formatInr(PAY_SUMMARY.original)}</p>
                  <p className="mt-2 text-sm text-zinc-400">
                    {PAY_SUMMARY.points.toLocaleString()} points
                  </p>
                </div>

                <motion.div
                  id="wl-af-payment-methods"
                  className="mt-6 space-y-3"
                  {...pulseAf(pulseId, "wl-af-payment-methods")}
                >
                  <button
                    type="button"
                    onClick={() => setPayMethod("crypto")}
                    className={`flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition ${
                      payMethod === "crypto"
                        ? "border-red-600/90 bg-red-950/20 shadow-[0_0_0_1px_rgba(220,38,38,0.35)]"
                        : "border-zinc-700/80 bg-zinc-950/40"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 rounded-full border-2 ${
                        payMethod === "crypto" ? "border-red-500 bg-red-500" : "border-zinc-500"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="m-0 text-base font-bold text-white">Crypto</p>
                        <span className="shrink-0 rounded-full border border-emerald-700/50 bg-emerald-950/60 px-2 py-0.5 text-[10px] font-bold text-emerald-400">
                          +5% off
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-red-300/80">Instant, fast, and safe transactions.</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPayMethod("fiat")}
                    className={`flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition ${
                      payMethod === "fiat"
                        ? "border-red-600/90 bg-red-950/20 shadow-[inset_0_0_0_1px_rgba(220,38,38,0.25)]"
                        : "border-zinc-700/80 bg-zinc-950/40"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 rounded-full border-2 ${
                        payMethod === "fiat" ? "border-red-500 bg-red-500" : "border-zinc-500"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-base font-bold text-white">Fiat</p>
                      <p className="mt-1 text-xs text-zinc-500">UPI / bank transfer</p>
                    </div>
                  </button>
                </motion.div>
              </div>

              <div className="mt-auto shrink-0 pt-3">
                <button
                  type="button"
                  className="w-full rounded-xl border border-red-900/50 bg-gradient-to-b from-red-950/90 to-black py-3.5 text-sm font-bold text-white"
                >
                  Continue
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);
